import i18n, { transformLanguageToAuth0Value } from 'translations/i18n'

import { StatusPage } from 'components/common/StatusPage'
import { logAnalyticsEvent } from 'utils/analytics'
import { useAuth0 } from 'utils/auth'
import { useTranslation } from 'react-i18next'

/**
 * @component
 * NotLogged - Displays authentication options for unauthenticated users.
 * 
 * This component shows login and signup options when users are not authenticated.
 * It includes analytics tracking for user interactions and supports:
 * - Login with redirect to current page
 * - Signup with redirect to current page
 * - Homepage link as alternative option
 * 
 * Uses StatusPage for consistent design with primary/secondary buttons and link button.
 * Tracks analytics events for login and signup clicks.
 * 
 * @example
 * <NotLogged />
 */
const NotLogged: React.FC = () => {
  const { loginWithRedirect } = useAuth0()
  const { t } = useTranslation(['notlogged', 'authentication'])
  const params = new URLSearchParams(window.location.search)

  const handleLogin = () => {
    logAnalyticsEvent('clicks_login', { location: 'NotLogged' })
    loginWithRedirect({
      appState: { targetUrl: window.location.pathname },
      authorizationParams: {
        mode: 'login',
        language: transformLanguageToAuth0Value(i18n.language),
        login_hint: params.has('userEmail') ? params.get('userEmail') ?? undefined : undefined,
      },
    })
  }

  const handleSignUp = () => {
    logAnalyticsEvent('clicks_sign_up', { location: 'NotLogged' })
    loginWithRedirect({
      appState: { targetUrl: window.location.pathname },
      authorizationParams: {
        mode: 'signUp',
        language: transformLanguageToAuth0Value(i18n.language),
        login_hint: params.has('userEmail') ? params.get('userEmail') ?? undefined : undefined,
      },
    })
  }

  return (
    <StatusPage
      title={t('title')}
      description={t('text')}
      primaryButton={{
        text: t('authentication:log_in'),
        onClick: handleLogin
      }}
      secondaryButton={{
        text: t('authentication:sign_up'),
        onClick: handleSignUp
      }}
      linkButton={{
        text: t('button'),
        to: '/',
        variant: 'link orange'
      }}
    />
  )
}

export default NotLogged
